<template>
  <el-row v-if="!permissionDenied" class="video-page-container">
    <el-row v-if="video !== null" :gutter="20">
      <el-col :md="16" :lg="17">
        <div class="player-section shadow-box">
          <div class="video-header">
            <h1 class="video-title" v-html="video.title" />
            <div class="video-meta">
              <div class="meta-left">
                <span class="meta-item"><i class="el-icon-video-play" /> {{ video.view }} 次播放</span>
                <span class="meta-item"><i class="el-icon-s-comment" /> {{ video.comment }} 评论</span>
                <span class="meta-item"><i class="el-icon-watch" /> {{ video.pubDate }}</span>
              </div>
              <div class="meta-right">
                <el-tag v-if="videoId && videoId.includes('BV')" size="mini" type="info" effect="plain">
                  <a target="_blank" :href="'https://bilibili.com/' + videoId" class="bili-link">B站源</a>
                </el-tag>
                <el-tag type="danger" size="small" effect="dark" class="live-tag">
                  <i class="el-icon-view" /> {{ realtimeViewCount }} 人正在看
                </el-tag>
              </div>
            </div>
          </div>

          <div class="video-content">
            <video-player-card
              v-if="video"
              :video-id="videoId"
              :video-data="video"
              :user-token="userToken"
              :send-event="sendEvent"
              @update-view-count="val => realtimeViewCount = val"
            />
          </div>

          <video-ops-card
            v-if="video"
            :video="video"
            :show-edit="showEdit"
            @like="likeVideo"
            @dislike="dislikeVideoWrapper"
            @collect="collection"
            @share="displayShareVideoDialog"
            @download="getDownloadUrl"
            @report="displayErrorReportDialog"
          />
        </div>

        <el-card class="info-card shadow-box">
          <div class="video-description" v-html="video.description" />
          <div class="video-tags">
            <el-tag v-for="(tag,index) in video.tags" :key="index" class="video-tag-item" size="small" effect="light" round>
              <router-link :to="'/video/tag/' + tag"># {{ tag }}</router-link>
            </el-tag>
          </div>
        </el-card>

        <user-comment-card v-if="videoId" :post-id="videoId" post-type="VideoPost" :current-user="currentUser" :screen-width="screenWidth" />
      </el-col>

      <el-col :md="8" :lg="7">
        <div class="side-container">
          <user-avatar-card v-if="user" :user-avatar="user" class="shadow-box mb-20" />
          <el-card class="recommend-card shadow-box">
            <div slot="header" class="recommend-header">
              <span class="title">接下来播放</span>
              <div class="auto-play">
                <span>自动播放</span>
                <el-switch v-model="autoPlay" size="mini" />
              </div>
            </div>
            <div class="recommend-list">
              <div v-for="(item,index) in similarVideos" :key="index" class="side-card-wrapper">
                <side-video-card :video="item" />
              </div>
            </div>
          </el-card>
        </div>
      </el-col>

      <el-dialog append-to-body :visible.sync="showPlaylistDialog" title="保存到收藏夹" width="400px" center>
        <el-table :data="playlist" highlight-current-row @current-change="handleCurrentRow">
          <el-table-column type="index" width="50" />
          <el-table-column prop="albumName" label="收藏夹名称" />
          <el-table-column prop="total" label="内容数" width="80" />
        </el-table>
        <div class="new-album-input">
          <el-input v-model="albumForm.albumName" placeholder="新建收藏夹..." size="medium">
            <el-button slot="append" icon="el-icon-plus" @click="onCreateAlbum">创建</el-button>
          </el-input>
        </div>
        <span slot="footer">
          <el-button @click="showPlaylistDialog = false">取消</el-button>
          <el-button type="primary" @click="addToPlaylist">确定收藏</el-button>
        </span>
      </el-dialog>

      <el-dialog append-to-body :visible.sync="showErrorReportDialog" title="视频报错" width="400px">
        <el-form :model="errorReportForm" label-position="top">
          <el-form-item label="请选择错误类型">
            <el-select v-model="errorReportForm.errorCode" placeholder="请选择" style="width: 100%">
              <el-option label="视频无封面" value="1" /><el-option label="视频无声音" value="2" />
              <el-option label="视频无画面" value="3" /><el-option label="视频无资源" value="4" />
              <el-option label="视频有广告" value="5" />
            </el-select>
          </el-form-item>
        </el-form>
        <span slot="footer">
          <el-button @click="showErrorReportDialog = false">取消</el-button>
          <el-button type="primary" @click="submitErrorReport">提交反馈</el-button>
        </span>
      </el-dialog>

      <el-dialog append-to-body :visible.sync="showShareVideoDialog" title="分享视频" width="500px">
        <div class="share-preview">
          <el-image lazy fit="cover" class="share-cover" :src="video.coverUrl" />
          <h4 v-html="video.title" />
        </div>
        <span slot="footer">
          <el-button type="primary" icon="el-icon-document-copy" @click="submitShareVideo">复制分享链接</el-button>
        </span>
      </el-dialog>
    </el-row>
  </el-row>
  <el-row v-else class="denied-container">
    <permission-denied-card :text-object="textObject" />
  </el-row>
</template>

<script>
import { similarVideo, videoInfo, downloadVideo, getShortUrl, videoErrorReport } from '@/api/vod'
import { doAlbumAction, createAlbum, getUserAlbumList } from '@/api/vod'
import { rcmdFeedback } from '@/api/vod'

import PermissionDeniedCard from '@/components/card/PermissionDeniedCard'
import VideoPlayerCard from 'components/card/VideoPlayerCard'
import SideVideoCard from 'components/card/SideVideoCard'
import UserAvatarCard from '@/components/card/UserAvatarCard'
import UserCommentCard from '@/components/card/UserCommentCard'
import VideoOpsCard from 'components/card/VideoOpsCard'

export default {
  name: 'VideoPage',
  components: { SideVideoCard, UserAvatarCard, PermissionDeniedCard, VideoPlayerCard, UserCommentCard, VideoOpsCard },
  data() {
    return {
      screenWidth: document.body.clientWidth,
      videoId: null,
      video: null,
      user: null,
      similarVideos: [],
      currentUser: { userId: -1, name: '游客', avatar: '', author: false },
      isCollected: false,
      showPlaylistDialog: false,
      playlist: [],
      showErrorReportDialog: false,
      errorReportForm: { videoId: null, errorCode: null },
      permissionDenied: false,
      textObject: { content: '视频', route: '/video' },
      autoPlay: false,
      albumForm: { albumName: null },
      currentRow: null,
      showShareVideoDialog: false,
      showEdit: true,
      userToken: null,
      sendEvent: true,
      realtimeViewCount: 0
    }
  },
  watch: {
    '$route'() { this.$router.go() }
  },
  created() {
    this.userToken = ''
    const loginUser = this.$store.state.userInfo
    if (loginUser) {
      this.currentUser = { userId: loginUser.userIdStr, name: loginUser.screenName, avatar: loginUser.avatarUrl, author: true }
    }
    this.videoId = this.$route.params.id
    this.getVideoInfo(this.videoId)
    this.getSimilarVideos(this.videoId)
  },
  methods: {
    handleCurrentRow(val) { this.currentRow = val },
    collection(video) {
      if (video.collected) return this.$message.info('取消收藏')
      this.showPlaylistDialog = true
      const queryInfo = {
        userId: this.currentUser.userId,
        postType: 2,
        pn: this.currentPage
      }
      getUserAlbumList(queryInfo).then(resp => { if (resp.code === 0) this.playlist = resp.data.list })
    },
    addToPlaylist() {
      if (!this.currentRow) return this.$message.info('请选择收藏夹')
      doAlbumAction({ albumId: this.currentRow.albumId, postId: this.videoId, action: 1 }).then(resp => {
        if (resp.code === 0) this.$notify.success({ title: '视频已收藏' })
        this.showPlaylistDialog = false
      })
    },
    getVideoInfo(videoId) {
      videoInfo(videoId).then(resp => {
        if (resp.code === 0) {
          this.video = resp.data
          this.user = resp.data.user
          document.title = resp.data.title
        } else if (resp.code === 2) {
          this.$router.push('/404')
        } else {
          this.permissionDenied = true
        }
      })
    },
    getSimilarVideos(videoId) {
      similarVideo(videoId).then(resp => { if (resp.code === 0) this.similarVideos = resp.data })
    },
    likeVideo(video) { this.$message.info('喜欢 ' + video.videoId) },
    dislikeVideoWrapper(video) {
      rcmdFeedback({ videoId: video.videoId }).then(resp => { if (resp.code === 0) this.$message.info('数据已提交') })
    },
    getDownloadUrl(videoId) {
      downloadVideo(videoId).then(resp => {
        if (resp.code === 0) window.open(resp.data.url, '_blank')
      })
    },
    onCreateAlbum() {
      createAlbum(this.albumForm).then(resp => { if (resp.code === 0) this.playlist.push(resp.data) })
      this.albumForm.albumName = null
    },
    displayErrorReportDialog() { this.errorReportForm.videoId = this.video.videoId; this.showErrorReportDialog = true },
    submitErrorReport() {
      videoErrorReport(this.errorReportForm).then(resp => {
        if (resp.code === 0) this.$notify.warning({ title: '提示', message: '视频错误已提交' })
        this.showErrorReportDialog = false
      })
    },
    displayShareVideoDialog() { this.showShareVideoDialog = true },
    submitShareVideo() {
      getShortUrl(this.video.videoId).then(resp => {
        if (resp.code === 0) {
          const content = window.location.origin + resp.data
          const input = document.createElement('input'); input.value = content; document.body.appendChild(input)
          input.select(); document.execCommand('Copy'); document.body.removeChild(input)
          this.$message.info('已成功复制到剪贴板')
        }
        this.showShareVideoDialog = false
      })
    }
  }
}
</script>

<style scoped>
.video-page-container { padding: 20px; background-color: #f4f4f5; min-height: 100vh; }
.shadow-box { box-shadow: 0 2px 12px 0 rgba(0,0,0,0.05) !important; border: none !important; border-radius: 8px !important; background: #fff; }
.player-section { background: #1a1a1a; padding: 20px; margin-bottom: 20px; }
.video-header { margin-bottom: 15px; }
.video-title { color: #fff; font-size: 20px; margin: 0 0 10px 0; font-weight: 600; }
.video-meta { display: flex; justify-content: space-between; align-items: center; color: #999; font-size: 13px; }
.meta-item { margin-right: 20px; }
.video-content { background: #000; border-radius: 4px; overflow: hidden; margin-bottom: 15px; }
.bili-link { color: inherit; text-decoration: none; }
.info-card { margin-bottom: 20px; padding: 10px; }
.video-description { font-size: 14px; color: #606266; line-height: 1.6; white-space: pre-wrap; }
.video-tags { margin-top: 20px; padding-top: 15px; border-top: 1px solid #ebeef5; }
.video-tag-item { margin-right: 10px; margin-bottom: 8px; border: none; background: #f0f2f5; }
.video-tag-item a { text-decoration: none; color: #409EFF; }
.side-container { position: sticky; top: 20px; }
.recommend-header { display: flex; justify-content: space-between; align-items: center; }
.recommend-header .title { font-weight: 600; font-size: 16px; }
.auto-play { font-size: 12px; color: #909399; display: flex; align-items: center; }
.auto-play span { margin-right: 8px; }
.side-card-wrapper { margin-bottom: 12px; transition: transform 0.2s; }
.side-card-wrapper:hover { transform: translateX(5px); }
.new-album-input { margin-top: 20px; }
.share-preview { text-align: center; }
.share-cover { width: 100%; height: 200px; border-radius: 8px; margin-bottom: 15px; }
.mb-20 { margin-bottom: 20px; }

@media screen and (max-width: 768px) {
  .video-page-container { padding: 10px; }
  .video-title { font-size: 16px; }
  .meta-right { display: none; }
}
</style>
